// Public taste profile at twoshot.xyz/<letterboxd-username> (§ public pages).
// A read-only mirror of the owner's solo sheet: same ink hero + perforated
// fields, but no account controls — just the taste, stamped with the snapshot
// time it was captured, and a CTA for a visitor to make their own.

import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Page, Skeleton } from "../components/Layout.tsx";
import {
  Sheet,
  Masthead,
  Tear,
  Field,
  Kicker,
  H3,
  Sub,
  Barcode,
  Count,
} from "../components/sheet.tsx";
import {
  RatingHistogram,
  DecadeBars,
  HeatStrip,
  ScoreDial,
} from "../components/Charts.tsx";
import { PosterCard } from "../components/brand.tsx";
import { computeTasteSignals, topLoved } from "../lib/tasteSignals.ts";
import { TasteBadges } from "../components/TasteBadges.tsx";
import { LetterboxdActivity } from "../components/LetterboxdActivity.tsx";
import { usePosters } from "../lib/usePosters.ts";
import { useCountUpProgress } from "../lib/useCountUp.ts";
import {
  getProfileByUsername,
  syncProfile,
  type PublicProfile as PublicProfileData,
} from "../lib/api.ts";

// Reserved top-level paths that are real routes, never usernames.
const RESERVED = new Set([
  "me",
  "onboarding",
  "with",
  "b",
  "auth",
  "dev",
  "api",
  "assets",
  "privacy",
]);

function fmtDate(iso: string): string {
  // YYYY-MM-DD or ISO timestamp -> "Jul 1, 2026", locale-independent enough.
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export default function PublicProfile() {
  const { username = "" } = useParams();
  const [profile, setProfile] = useState<PublicProfileData | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "missing">(
    "loading",
  );

  useEffect(() => {
    if (RESERVED.has(username.toLowerCase())) {
      setState("missing");
      return;
    }
    let cancelled = false;
    setState("loading");
    getProfileByUsername(username)
      .then((p) => {
        if (cancelled) return;
        setProfile(p);
        setState(p ? "ready" : "missing");
        // Lazy drift-sync AFTER first paint — never blocks the render. The
        // function throttles itself, so most views are a no-op; when it does
        // pull new logs we quietly swap in the fresher numbers.
        if (!p) return;
        syncProfile(username).then((r) => {
          if (cancelled || !r?.changed || !r.added) return;
          getProfileByUsername(username)
            .then((fresh) => {
              if (!cancelled && fresh) setProfile(fresh);
            })
            .catch(() => {});
        });
      })
      .catch(() => {
        if (!cancelled) setState("missing");
      });
    return () => {
      cancelled = true;
    };
  }, [username]);

  if (state === "loading") {
    return (
      <Page testid="screen-public-profile" wide bare>
        <div className="desk">
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      </Page>
    );
  }

  if (state === "missing" || !profile) {
    return (
      <Page testid="screen-public-profile" wide bare>
        <Sheet testid="public-missing">
          <Masthead right="No screening" />
          <Field>
            <Kicker>Nothing on this marquee</Kicker>
            <H3>No taste profile for “{username}”</H3>
            <Sub>
              This page shows up once someone uploads their Letterboxd export
              and their account is <code>{username}</code>.
            </Sub>
            <Link
              to="/"
              className="mt-4 inline-flex items-center rounded-pill bg-gold-500 px-5 py-2.5 text-sm font-semibold text-ink-900 no-underline transition-colors hover:bg-gold-400"
            >
              Make your own taste profile →
            </Link>
          </Field>
        </Sheet>
      </Page>
    );
  }

  return (
    <Page testid="screen-public-profile" wide bare>
      <PublicSheet profile={profile} />
    </Page>
  );
}

function PublicSheet({ profile }: { profile: PublicProfileData }) {
  const data = profile.profile_data;
  const name = profile.display_name;
  const handle = profile.letterboxd_username;
  const s = useMemo(() => computeTasteSignals(data), [data]);
  const prog = useCountUpProgress();
  const cu = (n: number) => Math.round(n * prog);
  const dial = Math.round((s.avgRating / 5) * 100);

  const loved = useMemo(() => topLoved(data, 14), [data]);
  const posters = usePosters(loved);

  const lbUrl = handle ? `https://letterboxd.com/${handle}/` : null;

  return (
    <Sheet testid="public-sheet">
      <Masthead right="Now showing" sub={handle ? `@${handle}` : undefined} />

      {/* Ink hero — critic type over a drift of their loved films */}
      <section
        data-testid="taste-signature"
        className="heroband bg-ink-900 text-paper-50"
      >
        {loved.length > 0 && (
          <div className="marq" aria-hidden>
            <div className="marqin">
              {[0, 1].map((dup) => (
                <div className="mset" key={dup}>
                  {loved.map((f) => (
                    <div className="mp" key={`${dup}-${f.uri}`}>
                      <PosterCard
                        title={f.title}
                        year={f.year}
                        poster={posters[f.uri]}
                        width="100%"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="marqveil" aria-hidden />
        <div className="relative z-[2] flex flex-col items-center gap-4 px-6 py-9 text-center sm:px-10">
          <div style={{ color: "var(--paper-50)" }}>
            <ScoreDial
              score={dial}
              size={104}
              label={`★${s.avgRating.toFixed(1)}`}
            />
          </div>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-stamp text-ink-300">
              {name}'s rating personality
            </p>
            <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tightish sm:text-4xl">
              {s.criticType} <span className="text-gold-500">critic</span>
            </h1>
            <p className="mx-auto mt-2 max-w-md text-sm text-paper-200/85">
              {s.criticBlurb}
            </p>
            <p
              className="mono-meta mx-auto mt-2 text-ink-300"
              data-testid="score-explainer"
            >
              ★{s.avgRating.toFixed(2)} / 5 average
            </p>
          </div>
        </div>
      </section>

      {/* Snapshot provenance — this is a point-in-time capture */}
      <div
        data-testid="snapshot-line"
        className="flex flex-wrap items-baseline gap-x-2 gap-y-1 bg-paper-100 px-6 py-3 sm:px-10"
      >
        <span className="font-mono text-[11px] font-bold uppercase tracking-stamp text-red-600">
          Snapshot
        </span>
        <span className="text-sm text-ink-600">
          Library exported {fmtDate(data.exportedAt)} · captured on twoshot{" "}
          {fmtDate(profile.updated_at)}
        </span>
        {/* The drift layer, stated plainly: the export is the baseline, these
            came from the public diary afterwards. */}
        {!!profile.synced_count && (
          <span
            data-testid="sync-line"
            className="text-sm font-semibold text-red-600"
          >
            · +{profile.synced_count.toLocaleString()} logged since
          </span>
        )}
      </div>

      {/* The ledger */}
      <section className="px-6 py-6 sm:px-10">
        <div className="flex flex-wrap gap-x-10 gap-y-4">
          <Count n={cu(data.counts.watched)} label="Watched" />
          <Count n={cu(data.counts.rated)} label="Rated" tone="red" />
          <Count
            n={`★${s.avgRating.toFixed(2)}` as unknown as number}
            label="Average"
            tone="gold"
          />
          <Count
            n={cu(data.counts.watchlist)}
            label="Watchlist"
            tone={data.counts.watchlist ? "clay" : "muted"}
          />
        </div>
      </section>

      <Tear />

      {/* Blend CTA — up top: it's the reason a visitor is here */}
      <Field testid="section-blend-cta">
        <Kicker>See how you two line up</Kicker>
        <H3>Blend your taste with {name}</H3>
        <Sub>
          Upload your own Letterboxd export and get a shared canon, disputes,
          and cross-recommendations.
        </Sub>
        <Link
          to={`/with/${profile.share_slug}`}
          data-testid="action-blend-with"
          className="mt-4 inline-flex items-center rounded-pill bg-gold-500 px-5 py-2.5 text-sm font-semibold text-ink-900 no-underline transition-colors hover:bg-gold-400"
        >
          Blend with {name} →
        </Link>
      </Field>

      <Tear />

      {/* Their taste in one line — read-only here */}
      {profile.summary && (
        <>
          <Field testid="section-taste-summary">
            <Kicker>In one line</Kicker>
            <p className="mt-2 font-display text-xl font-medium italic leading-snug text-ink-900 sm:text-2xl">
              “{profile.summary}”
            </p>
          </Field>
          <Tear />
        </>
      )}

      {/* Taste stickers (cached LLM set; deterministic fallback) */}
      <TasteBadges
        profile={data}
        badges={profile.badges}
        heading="Their type"
      />

      {/* Live Letterboxd: curated favourites + recent watches (scraped, fresh).
          Renders its own Tears; nothing at all when there's no handle/data. */}
      {handle && (
        <LetterboxdActivity
          username={handle}
          name={name.trim().split(/\s+/)[0] || name}
        />
      )}

      <Tear />

      {/* Signature gauges */}
      <Field>
        <Kicker>Their signature</Kicker>
        <H3>What defines this taste</H3>
        <div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-5 sm:grid-cols-4">
          <Gauge
            value={s.definingDecade ?? "—"}
            label="Home decade"
            sub={
              s.definingDecade
                ? `${Math.round(s.definingDecadeShare * 100)}% of watches`
                : "no data"
            }
            tone="red"
          />
          <Gauge
            value={s.erasSpanned ? `${s.erasSpanned}` : "—"}
            label="Eras spanned"
            sub="decades of film"
            tone="ink"
          />
          <Gauge
            value={s.backlog.toLocaleString()}
            label="Backlog"
            sub="on the watchlist"
            tone="gold"
          />
          <Gauge
            value={`${Math.round(s.ratedShare * 100)}%`}
            label="Rated"
            sub="of the library"
            tone="clay"
          />
        </div>
      </Field>

      <Tear />

      <Field>
        <Kicker>How they score</Kicker>
        <H3>Rating personality</H3>
        <div className="mt-4">
          <RatingHistogram hist={data.aggregates.ratingHist} />
        </div>
      </Field>

      <Tear />

      <Field>
        <Kicker>Across the century</Kicker>
        <H3>By decade</H3>
        <div className="mt-4">
          <DecadeBars
            byDecade={data.aggregates.byDecade}
            username={handle ?? undefined}
          />
        </div>
      </Field>

      <Tear />

      <Field>
        <Kicker>Their logging habit</Kicker>
        <H3>Logged per month</H3>
        <div className="mt-4">
          <HeatStrip loggedByMonth={data.aggregates.loggedByMonth} />
        </div>
      </Field>

      {/* Footer */}
      <div className="flex items-center gap-6 px-6 py-5 sm:px-10">
        <p className="mono-meta flex-1 leading-relaxed text-ink-400">
          {lbUrl ? (
            <>
              Follows{" "}
              <a
                href={lbUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                letterboxd.com/{handle}
              </a>
            </>
          ) : (
            "A twoshot taste snapshot"
          )}
        </p>
        <Barcode className="hidden w-[200px] shrink-0 sm:block" />
      </div>
    </Sheet>
  );
}

function Gauge({
  value,
  label,
  sub,
  tone,
}: {
  value: string;
  label: string;
  sub: string;
  tone: "red" | "gold" | "clay" | "ink";
}) {
  const color =
    tone === "red"
      ? "text-red-600"
      : tone === "gold"
        ? "text-gold-600"
        : tone === "clay"
          ? "text-clay-500"
          : "text-ink-900";
  return (
    <div>
      <div
        className={`font-display text-2xl font-extrabold leading-tight tracking-tightish ${color}`}
      >
        {value}
      </div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-stamp text-ink-500">
        {label}
      </div>
      <div className="text-[11px] text-ink-400">{sub}</div>
    </div>
  );
}
